import { useEffect, useState } from "react";
import { CloudSun } from "lucide-react";
import { iconFor } from "@/lib/iconMap";
import { api } from "@/lib/api";

/**
 * Compact floating weather chip showing The Crags / Plettenberg Bay current
 * conditions. Mounted globally in AppShell so it appears on every page,
 * pinned to the top-right corner. Refreshes every 10 minutes (matching the
 * server-side cache TTL).
 *
 * Variants:
 *  - "chip"  → small pill, fixed top-right (default, used in AppShell)
 *  - "inline"→ full strip with high/low/wind/humidity, used inside cards
 */
export function WeatherChip({ variant = "chip" }) {
  const [w, setW] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await api.get("/weather");
      setW(res.data);
    } catch (e) {
      // Soft-fail — keep last known value if any
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 10 * 60 * 1000); // 10 min
    return () => clearInterval(t);
  }, []);

  if (variant === "inline") {
    if (loading && !w) {
      return (
        <div className="text-xs text-muted-foreground" data-testid="weather-inline-loading">
          Loading weather…
        </div>
      );
    }
    if (!w || w.error) return null;
    const WIcon = iconFor(w.icon);
    return (
      <div
        className="flex items-center gap-3 rounded-lg border border-[color:var(--border-hairline)] bg-[color:var(--surface-glass)] backdrop-blur-md px-3 py-2"
        data-testid="weather-inline"
      >
        <WIcon className="h-6 w-6 shrink-0 text-[color:var(--accent-brass)]" strokeWidth={1.5} />
        <div className="flex flex-col leading-tight">
          <div className="flex items-baseline gap-2">
            <span className="font-serif text-xl tabular-nums" data-testid="weather-inline-temp">
              {Math.round(w.temperature_c)}°C
            </span>
            <span className="text-xs text-muted-foreground">{w.condition}</span>
          </div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
            {w.location}
            {w.high_c != null && w.low_c != null && (
              <span className="ml-2 normal-case tracking-normal">
                H {Math.round(w.high_c)}° / L {Math.round(w.low_c)}°
              </span>
            )}
            {w.wind_kmh != null && (
              <span className="ml-2 normal-case tracking-normal">· Wind {Math.round(w.wind_kmh)} km/h</span>
            )}
            {w.humidity_pct != null && (
              <span className="ml-2 normal-case tracking-normal">· {Math.round(w.humidity_pct)}% RH</span>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Default "chip" — small floating pill, top-right of viewport (desktop only;
  // mobile uses an inline version embedded in the mobile header instead).
  if (loading && !w) {
    return (
      <div
        className="fixed top-3 right-4 z-30 hidden lg:flex items-center gap-1.5 rounded-full bg-background/85 backdrop-blur-md border border-border px-2.5 py-1 text-[11px] text-muted-foreground shadow-sm"
        data-testid="weather-chip-loading"
      >
        <span className="animate-pulse inline-flex items-center gap-1"><CloudSun className="h-3.5 w-3.5" strokeWidth={1.5} /> …</span>
      </div>
    );
  }
  if (!w || (w.error && w.temperature_c == null)) return null;

  const WIcon = iconFor(w.icon);
  return (
    <div
      className="fixed top-3 right-4 z-30 hidden lg:flex items-center gap-1.5 rounded-full bg-[color:var(--surface-glass-strong)] backdrop-blur-md border border-[color:var(--border-hairline)] px-2.5 py-1 text-[11px] shadow-sm hover:bg-[color:var(--surface-hover)] transition-colors"
      title={`${w.condition}. ${w.location}`}
      data-testid="weather-chip"
    >
      {w.flag && <span className="shrink-0" aria-hidden="true">{w.flag}</span>}
      <WIcon className="h-3.5 w-3.5 shrink-0 text-[color:var(--accent-brass)]" strokeWidth={1.5} />
      <span className="font-medium tabular-nums" data-testid="weather-chip-temp">
        {Math.round(w.temperature_c)}°C
      </span>
      <span className="text-muted-foreground border-l border-border/60 pl-1.5 leading-none">
        {w.location}
      </span>
    </div>
  );
}


/**
 * Ultra-compact weather pill for embedding inside the mobile header (no
 * fixed positioning — flows in normal layout).
 */
export function WeatherChipMobile() {
  const [w, setW] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get("/weather");
        setW(res.data);
      } catch (e) { /* silent */ }
    };
    load();
    const t = setInterval(load, 10 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  if (!w || w.temperature_c == null) return null;
  const WIcon = iconFor(w.icon);
  return (
    <div
      className="inline-flex items-center gap-1 rounded-full bg-[color:var(--surface-glass)] px-2 py-0.5 text-[11px]"
      title={`${w.condition}. ${w.location}`}
      data-testid="weather-chip-mobile"
    >
      {w.flag ? <span aria-hidden="true">{w.flag}</span> : <WIcon className="h-3 w-3 text-[color:var(--accent-brass)]" strokeWidth={1.5} />}
      <span className="tabular-nums font-medium">{Math.round(w.temperature_c)}°</span>
      <span className="text-muted-foreground">{w.location_short || w.location || ""}</span>
    </div>
  );
}
